import React, { useState, useEffect } from 'react'
import { fetchMarketPackages } from '../../services/GraphQlClientService'
import { Header2, Text, SectionHeader } from '../../styles/Components'
import Button from '../../components/Button'
import { PackagesWrapper } from '../../components/packages/styles/index'
import MarketSelector from '../../components/MarketSelector'
import { gsColors as c, gsColors } from '../../styles/Variables'
import SelectProductButton from '../../components/SelectProductButton'
import FAQ from '../../faq'
import GroupSelector from '../../components/group-selector'
import Head from 'next/head'
import { useMarketContext } from '../../components/MarketContext'
import { generateGiftCardFinePrint } from '../../helpers'
import Hero from '../../components/Hero'
import HeroImage from '../../../public/GS_packages_hero.jpeg'
import { useRouter } from 'next/router'

const FAQ_DATA_PACKAGES = [
  {
    id: 1,
    title: 'Why do I have an additional charge when my package is applied to my service?',
    body: 'There are a few scenarios where you may pay in addition to your existing package credits that have been applied to your appointment. If you have a package of blowouts and book for an updo which costs more, additional credits will be subtracted from your package to cover the cost of the updo. This will leave your package with an uneven amount of credits. When you use the last of your credits you will have an additional charge to cover what the credits did not cover in that appointment. If you have a blowout package and book a blowout plus a manicure, only the blowout package will apply and you will be charged for the manicure.',
  },
  {
    id: 2,
    title: 'How do packages work?',
    body: 'When you purchase a package you\'re essentially purchasing service credits at a discounted rate. For example, the cost for a package of 5 blowouts will be at a discounted rate plus the gratuity of the full service, the support fee, and tax if applicable. The credits you are given will reflect the full price of the services in your account once the transaction is complete.',
  },
  {
    id: 3,
    title: 'Why should I purchase a package?',
    body: 'Glamsquad isn\'t just for special occasions. Packages are a great way to save money for those who want to make looking and feeling great a regular part of their routine. Glamsquad package credits never expire!',
  },
]

const FAQ_URL = '/faq/category/service-packages'

const Packages = () => {
  const { query } = useRouter()
  const { markets, selectedMarket, setSelectedMarket } = useMarketContext()
  const [packages, setPackages] = useState({})
  const [selectedPackageGroup, setSelectedPackageGroup] = useState('')
  const [selectedPackage, setSelectedPackage] = useState()

  const queryPackageGroup = query?.group

  // load packages for selected market
  useEffect(() => {
    if (!selectedMarket) {
      return
    }

    fetchMarketPackages(selectedMarket.id).then(packages => {
      // const groupedPackages = groupBy(packages, 'productGroup.name')
      const groupedPackages = packages.reduce((acc, productPackage) => {
        const groupName = productPackage.productGroup.name.toLowerCase()
        if (!acc[groupName]) {
          acc[groupName] = [productPackage]
        } else {
          acc[groupName].push(productPackage)
        }

        return acc
      }, {})

      setPackages(groupedPackages)
      setSelectedPackageGroup(Object.keys(groupedPackages)[0])
    })
  }, [selectedMarket])

  // set selected package group from query param if it exists for the selected market
  useEffect(() => {
    const group = queryPackageGroup?.toLowerCase().replace(/-/g, ' ')
    const groupExists = packages.hasOwnProperty(group)
    if (group && groupExists) {
      setSelectedPackageGroup(group)
    }
  }, [queryPackageGroup, packages])

  const finePrint = generateGiftCardFinePrint('package', packages, selectedPackageGroup)

  return (<>
    <Head>
      <title>Packages | Glamsquad</title>
      <link rel='canonical' href='https://glamsquad.com/packages' />
    </Head>
    <PackagesWrapper>
      <Hero
        className='hero-class'
        header='BUNDLE YOUR BEAUTY' 
        subheader='Our beauty packages are the perfect way to enjoy more of the signature services you love at amazing prices.' 
        image={HeroImage}
      />
      <div className="gift-link-wrapper">
        Want to gift a package?
        <Button
          outline
          color={c.black}
          path="/gift-card"
          className='visit-gift-button'
        >
          Visit Gifting
        </Button>
      </div>
      <div className="content-container">
        <div className="packages-content">
          <div className="package-steps">
            <div className="step-1">
              <SectionHeader small margin="15 0">
                Step 1
              </SectionHeader>
              <Header2 align='center'>Select location</Header2>
              <MarketSelector
                markets={markets}
                selected={selectedMarket}
                setSelected={setSelectedMarket}
              />
            </div>

            <div className="step-2" id="main-content" tabIndex="0">
              <SectionHeader small margin="15 0">
                Step 2
              </SectionHeader>
              <Header2 align='center'>Choose a package</Header2>
              <Text
                className="subtitle"
                margin="15px 0"
                color={c.meredithGrey}
                variant="small"
              >
                {finePrint}
              </Text>

              <GroupSelector
                groups={packages}
                setSelectedGroup={(group) => {
                  setSelectedPackage(null)
                  setSelectedPackageGroup(group)
                }}
                selectedGroup={selectedPackageGroup}
              />

              <div className="packages-container">
                {selectedPackageGroup &&
                  packages &&
                  Object.keys(packages).includes(selectedPackageGroup) &&
                  packages[selectedPackageGroup].map(product => (
                    <div 
                      id={product.sku} 
                      key={product.id} 
                      className={['package-card', product.id === selectedPackage?.id && 'selected-package'].join(' ')} 
                      onClick={() => setSelectedPackage(product)}
                      role='button'
                    >                      
                      <div className="card-content">    
                        <Text variant='medium' weight='600'>{product.name}</Text>
                        <span style={{ display: 'flex' }}>
                          <Text color={gsColors.cobalt} weight='600' margin='0 8px 0 0'>{product.discountLabel}</Text>
                          <Text>(${product.creditAmount} value)</Text>
                        </span>
                        {product.description && <Text fontSize={13} color={c.meredithGrey} lineHeight='18px'>{product.description}</Text>}
                      </div>
                      <Text>${product.price}</Text>
                    </div>
                  ))}
              </div>

              <div className="select-product-button">
                <SelectProductButton
                  color={c.sunsetOrange}
                  product={selectedPackage}
                  fromPath="packages"
                />
              </div>
            </div>

          </div>
        </div>
      </div>

      <div className="faq-wrapper">
        <FAQ
          data={FAQ_DATA_PACKAGES}
          url={FAQ_URL}
        />
      </div>
    </PackagesWrapper>
  </>
  )
}

export default Packages
